<template>

  <v-container fluid class="pa-0 ma-0 delivery-file-uploader">
    <!-- File selector -->
    <v-row class="pa-0 ma-0">
      <v-col class="pa-0 ma-0 d-flex flex-column align-left justify-center image-upload-area">
        <v-file-input
            ref="image-input"
            solo
            class="camera-upload-input-area"
            :accept="allowedFileTypes"
            prepend-icon="false"
            hide-details="true"
            :label="label"
            @click:clear="clear"
            @change="setFile" />
      </v-col>
    </v-row>

    <!-- File preview -->
    <v-row class="pa-0 ma-0" v-if="file">
      <v-col class="pa-0 ma-0">
        <div class="file-preview-container">
          <iframe
              v-if="isPdf && isIOS"
              class="pdf-preview"
              :src="pdfPreviewSrc"
              title="Selected FA card PDF" />
          <embed
              v-else-if="isPdf"
              class="pdf-preview"
              :src="pdfPreviewSrc"
              type="application/pdf" />
          <img v-else id="img" :src="imageData" alt="Selected FA card" />
        </div>
      </v-col>
    </v-row>

    <!-- Actions -->
    <v-row class="pa-0 ma-0 mt-3" v-if="file">
      <v-col style="text-align:center" class="pa-0 ma-0">
        <v-btn text v-if="showCancel && !isUploading" @click="cancel">Cancel</v-btn>
        <v-btn text v-if="showClear && file && !isUploading" @click="clear">Clear</v-btn>
        <v-btn
            text
            color="primary"
            width="100"
            :disabled="!file"
            @click="save">
          <div class="d-flex align-center" v-if="isUploading">
            <v-progress-circular indeterminate :size="18" color="primary" />
            <div class="ml-3">Uploading...</div>
          </div>
          <span v-else>Save</span>
        </v-btn>
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
// import { createWorker } from 'tesseract.js'

export default {
  name: 'DeliveryFileUploader',

  data () {
    return {
      logDataExtraction: false, // NOTE - this is for debugging only !!!
      worker: null,
      file: null,
      imageData: null,
      extractedData: {},
      isInitializing: false,
      isUploading: false,
      // ---------------- Resized file for upload
      fileToUpload: null,
      uploadMaxHeight: 960,
      uploadMaxWidth: 960
    }
  },

  props: {
    orderId: {
      type: [String, Number],
      required: true
    },
    assetKey: {
      type: String,
      required: true
    },
    enableUpload: {
      type: Boolean,
      default: true
    },
    allowedFileTypes: {
      type: String,
      default: 'application/pdf,image/*'
    },
    showClear: {
      type: Boolean,
      default: false
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    label: {
      type: [String, Boolean],
      default: 'false'
    }
  },

  computed: {
    isPdf () {
      return this.isPdfFile(this.file)
    },
    isIOS () {
      const userAgent = navigator.userAgent || ''
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent)
      const isIPadViaDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
      return isIOSDevice || isIPadViaDesktopMode
    },
    pdfPreviewSrc () {
      return this.imageData
        ? `${this.imageData}#toolbar=0&navpanes=0&scrollbar=0`
        : null
    }
  },

  async mounted () {
    /*
    this.isInitializing = true

    this.worker = await createWorker({
      // logger: m => console.log(m),
      errorHandler: err => this.abort(err)
    })
    await this.worker.loadLanguage('eng')
    await this.worker.initialize('eng')
    await this.worker.setParameters({
      tessedit_ocr_engine_mode: 'OEM_DEFAULT',
      tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZÑ)/ ',
      preserve_interword_spaces: '0'
    })

    this.isInitializing = false
    */
    this.$emit('initialized')
  },

  async beforeDestroy () {
    this.clear()
    if (this.worker) await this.worker.terminate()
  },

  methods: {
    trigger () {
      const inputElement = this.$refs['image-input']
      if (inputElement) inputElement.$refs.input.click()
    },

    abort (err) {
      this.$emit('aborted', err)
    },

    clear () {
      this.releasePreviewUrl()
      this.file = null
      this.fileToUpload = null
      this.imageData = null
      this.extractedData = {}
    },

    cancel () {
      this.clear()
      this.$emit('canceled')
    },

    async extractFaCardInformation (fileData, options) {
      const imgHeight = options.height
      const imgWidth = options.width
      const clipHeight = imgHeight / 2

      // Perform image recognize (using bottom half of the image)
      const { data: { text } } = await this.worker.recognize(
        fileData,
        {
          rectangle: { top: clipHeight, left: 0, width: imgWidth, height: clipHeight }
        }
      )

      if (this.logDataExtraction) {
        console.log(`extracting from image h:${imgHeight} w:${imgWidth}`)
        console.log(`extracted text top:${clipHeight} w:${imgWidth} h:${imgHeight}:`)
        console.log('')
        console.log('--------------------------------')
        console.log(text)
        console.log('--------------------------------')
        console.log('')
      }

      // Extract lines of text
      const lines = text.split(/\r?\n/)
      if (this.logDataExtraction) console.log('All lines:', lines)

      // Find last line that does not contain a number, just before a line with numbers (i.e. the DOB)
      let possibleName = ''
      let previousAllText = null
      const containsNumber = /\d/
      for (const line of lines) {
        if (this.logDataExtraction) console.log('inspecting line:', line)
        const noNumbers = !containsNumber.test(line)
        if (noNumbers) {
          if (this.logDataExtraction) console.log('--- no numbers:', line)
          const formattedName = this.$root.utils.toStartCase(line.toLowerCase())
          previousAllText = formattedName
          possibleName = formattedName
        } else if (previousAllText) {
          if (this.logDataExtraction) {
            console.log('--- has numbers:', line)
            console.log('--- previousAllText:', previousAllText)
          }
          possibleName = previousAllText
          break
        }
      }

      if (this.logDataExtraction) {
        console.log('')
        console.log('--------------------------------')
        console.log('Name:', possibleName)
        console.log('--------------------------------')
      }

      // Save extracted agent name
      this.extractedData.receiving_agent_name = possibleName

      // Extract FA card number
      // const regex = /\b[A-Z0-9)]{20}\b/g
      const regex = /[A-Z0-9)]{20}\b/
      const matches = text.match(regex)
      // console.log('FA card matches:', matches)

      const firstMatch = (matches && matches.length > 0) ? matches[0] : ''
      const faCardNumber = firstMatch.replace(/\)/g, 'J')

      if (this.logDataExtraction) {
        console.log('')
        console.log('--------------------------------')
        console.log('FA card #:', faCardNumber)
        console.log('--------------------------------')
        console.log('')
      }

      // Save extracted FA card number
      this.extractedData.receiving_agent_fa_license_number = faCardNumber
    },

    async setFile (file) {
      if (!file) return false

      this.releasePreviewUrl()

      this.file = this.normalizePdfFile(file)
      this.imageData = this.isPdf
        ? URL.createObjectURL(this.file)
        : await this.readFile(this.file)
    },

    isPdfFile (file) {
      if (!file) return false

      const fileType = (file.type || '').toLowerCase()
      return fileType === 'application/pdf' ||
        fileType === 'application/x-pdf' ||
        /\.pdf$/i.test(file.name || '')
    },

    normalizePdfFile (file) {
      if (!this.isPdfFile(file) || file.type === 'application/pdf') return file

      // Some mobile file pickers omit or misreport a PDF's MIME type. Ensure
      // the browser sends the correct Content-Type when uploading to storage.
      return new File([file], file.name, {
        type: 'application/pdf',
        lastModified: file.lastModified
      })
    },

    releasePreviewUrl () {
      if (this.imageData && this.imageData.startsWith('blob:')) {
        URL.revokeObjectURL(this.imageData)
      }
    },

    async readFile (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (event) => {
          resolve(event.target.result)
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    async createImage (imageData) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          resolve(img)
        }
        img.onerror = reject
        img.src = imageData
      })
    },

    async resizeImageAndExtractData (options = {}) {
      const maxWidth = options.max_width || this.uploadMaxWidth
      const maxHeight = options.max_height || this.uploadMaxHeight
      if (!this.imageData) return false
      if (this.isPdf) return this.file

      // Generate image to determine reduced size
      const originalImage = await this.createImage(this.imageData)

      // Try to extract info from uploaded image
      // if (this.logDataExtraction) {
      //   console.log('---------------------------------------------')
      //   console.log('Parsing extracted text from image recognize')
      //   console.log('---------------------------------------------')
      //   console.log('o_h:', originalImage.height)
      //   console.log('o_w:', originalImage.width)
      // }
      // await this.extractFaCardInformation(this.imageData, { height: originalImage.height, width: originalImage.width })

      return new Promise((resolve) => {
        // Generate new dimensions
        const aspectRatio = originalImage.width / originalImage.height
        let width = originalImage.width
        let height = originalImage.height

        if (width > maxWidth) {
          width = maxWidth
          height = width / aspectRatio
        }
        if (height > maxHeight) {
          height = maxHeight
          width = height * aspectRatio
        }

        // Render element to build file object
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(originalImage, 0, 0, width, height)
        canvas.toBlob(
          (blob) => {
            const file = new File([blob], this.file.name, {
              type: this.file.type
            })
            resolve(file)
          },
          this.file.type,
          0.9
        )
      })
    },

    async save () {
      let uploaded = false
      let response = null
      let error = null

      // Perform upload, if enabled
      if (this.enableUpload) {
        this.isUploading = true
        try {
          response = await this.uploadAsset()
          const status = response?.status || 'unknown'
          if (status === 200) uploaded = true
          else error = response?.statusText || `There was a problem uploading to storage (${status})`
        } catch (err) {
          error = err.text || err
          console.error('Error uploading asset =>', err)
        }
        this.isUploading = false
      }

      // Emit results
      this.$emit('saved', {
        order_id: this.orderId,
        asset_key: this.assetKey,
        file: this.file,
        image_data: this.imageData,
        extracted_data: this.extractedData,
        uploaded,
        response,
        error
      })
    },

    async uploadAsset () {
      const orderId = this.orderId
      const assetKey = this.assetKey
      const file = this.file
      if (!assetKey || !orderId || !file) return false

      let uploadInfo = null
      try {
        // Resize the image and extract FA card info (saved as fileToUpload)
        const fileToUpload = await this.resizeImageAndExtractData()
        if (!fileToUpload) return false

        // Handle order-related upload
        uploadInfo = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${orderId}/asset/uploadUrl`,
          opts: {
            params: { asset_key: assetKey }
          },
          to_model: false
        })

        return (uploadInfo)
          ? fetch(uploadInfo.url, { method: 'PUT', body: fileToUpload, signal: AbortSignal.timeout(45000) })
          : false
      } catch (err) {
        console.error(`Error generating upload info for asset: ${assetKey} =>`, err)
        throw err
      }
    }
  }
}
</script>

<style>
  .delivery-file-uploader .file-preview-container {
    border: 1px solid #525252;
    padding: 8px;
    min-height: 20px;
    text-align: center;
  }

  .delivery-file-uploader .file-preview-container img {
    max-width:50%;
    max-height:220px;
  }

  .delivery-file-uploader .pdf-preview {
    border: 0;
    height: 400px;
    width: 100%;
  }

  .delivery-file-uploader .icon-take-photo {
    font-size: 24px !important;
  }

  .delivery-file-uploader .image-upload-area {
    padding: 12px 0 0 0 !important;
  }

  .delivery-file-uploader .camera-upload-input-area {
    /* padding: 0 34px 0 0 !important; */
    display: none;
  }
  .delivery-file-uploader .camera-upload-input-area .v-input__slot {
    cursor: pointer;
    background: none !important;
    box-shadow: none !important;
  }
</style>
